import { supabase } from '../lib/supabase'
import type { User, UserUpdate } from '../types/database.types'

export interface SignUpData {
  email: string
  password: string
  fullName?: string
}

export interface SignInData {
  email: string
  password: string
}

export const authService = {
  /**
   * Sign up a new user
   */
  async signUp({ email, password, fullName }: SignUpData) {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: fullName,
        },
      },
    })

    if (error) throw error
    return data
  },

  /**
   * Sign in with email and password (with timeout protection)
   */
  async signIn({ email, password }: SignInData) {
    const signInPromise = supabase.auth.signInWithPassword({ email, password })
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error('AUTH_TIMEOUT')), 5000)
    })

    const { data, error } = (await Promise.race([signInPromise, timeoutPromise])) as Awaited<ReturnType<typeof supabase.auth.signInWithPassword>>

    if (error) {
      console.error('[authService] Sign in error:', error)
      throw error
    }

    return data
  },

  /**
   * Sign out the current user
   * Uses global scope to revoke ALL sessions for this user
   */
  async signOut() {
    const { error } = await supabase.auth.signOut({ scope: 'global' })
    if (error) throw error
  },

  /**
   * Get the current session
   */
  async getSession() {
    const { data, error } = await supabase.auth.getSession()
    if (error) throw error
    return data.session
  },

  /**
   * Get the current user
   */
  async getUser() {
    const { data, error } = await supabase.auth.getUser()
    if (error) throw error
    return data.user
  },

  /**
   * Get user profile from the users table
   */
  async getProfile(userId: string): Promise<User> {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('id', userId)
      .single()

    if (error) throw error
    return data
  },

  /**
   * Update user profile
   */
  async updateProfile(userId: string, updates: UserUpdate): Promise<User> {
    const { data, error } = await supabase
      .from('users')
      .update(updates)
      .eq('id', userId)
      .select()
      .single()

    if (error) throw error
    return data
  },

  /**
   * Send password reset email
   */
  async resetPassword(email: string) {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    })

    if (error) throw error
  },

  /**
   * Update password (after reset)
   */
  async updatePassword(newPassword: string) {
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    })

    if (error) throw error
  },

  /**
   * Listen to auth state changes
   */
  onAuthStateChange(
    callback: (
      event: string,
      session: ReturnType<typeof supabase.auth.getSession> extends Promise<infer T> ? T extends { data: { session: infer S } } ? S : never : never
    ) => void
  ) {
    return supabase.auth.onAuthStateChange(callback as Parameters<typeof supabase.auth.onAuthStateChange>[0])
  },

  /**
   * Sign in with magic link (passwordless)
   */
  async signInWithMagicLink(email: string) {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    })

    if (error) throw error
  },
}
